import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as NotificationsActions from '../../../actions/notifications'
import * as ChannelsSelectors from '../../../reducers/content/channels'
import type Channel from '../../../types/Channel'
import type Device from '../../../types/DeviceMain'
import * as DeviceActions from '../../../actions/device/device'
import { ContentPushConfirmation, SettingsPushConfirmation } from './PushConfirmation'

interface ChannelDevicesTitleProps {
    isLoading?: boolean
    isOpen?: boolean
}

export default function ChannelDevicesTitle({ isLoading = false, isOpen = false }: ChannelDevicesTitleProps): JSX.Element {
    const [percentage, setPercentage] = React.useState<undefined | number>()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const selectedChannel: Channel | undefined = useSelector(ChannelsSelectors.getSelectedChannel)
    const devices: Device[] = selectedChannel?.devices || []
    const devicesCount: number = devices.length

    const pushToAllDevices = (kind: 'settings' | 'content') => async () => {
        // Initialize progress
        setPercentage(0)

        await Promise.all(
            devices.map(async ({ id }, index): Promise<void> => new Promise(
                (resolve) => {
                    setTimeout(async () => {
                        if (id) {
                            switch (kind) {
                                case 'content':
                                    await dispatch(DeviceActions.updateContentViaApi(id))
                                    break
                                case 'settings':
                                    await dispatch(DeviceActions.updateSettingsViaApi(id))
                                    break
                            }
                        }

                        // Update progress
                        const newPercentage = (index + 1) / devices.length

                        // Update the percentage only if it's higher than the previous percentage
                        // This avoids race conditions on the percentage
                        if (newPercentage > (percentage ?? 0)) {
                            setPercentage(newPercentage)
                        }
                        resolve()
                    }, index * 200)
                })
            )
        )

        let title: string = ''
        let body: string = ''

        switch (kind) {
            case 'content':
                title = t('message-content-update-multiple', { context: 'title' })
                body = t('message-content-update-multiple', {
                    context: 'body',
                    count: devicesCount,
                })
                break
            case 'settings':
                title = t('message-settings-update-multiple', { context: 'title' })
                body = t('message-settings-update-multiple', {
                    context: 'body',
                    count: devicesCount,
                })
                break
        }

        if (title && body) {
            await dispatch(NotificationsActions.notify({ title, body }))
        }


        setPercentage(undefined)
    }

    let label

    if (isOpen && !isLoading) {
        label = t('channel-devices-count', {
            count: devicesCount,
        })
    } else {
        label = t('devices')
    }

    const storesList: string[] = devices.reduce((out: string[], { store_code: storeCode }: Device): string[] => {
        if (out.includes(storeCode)) {
            return out
        }

        return [...out, storeCode]
    }, [] as string[])
    const storeCount: number = storesList.length

    return (
        <>
            {label}
            {isOpen && storeCount > 0 && (
                <>
                    &nbsp;
                    <span style={{ fontWeight: 'normal' }}>
                        &#40;
                        {t('stores-count', { count: storeCount })}
                        &#41;
                    </span>
                </>
            )}
            {devices?.length > 0 && (
                <>
                    <ContentPushConfirmation
                        devicesCount={devicesCount}
                        isOpen={isOpen}
                        storeCount={storeCount}
                        percentage={percentage}
                        pushToAllDevices={pushToAllDevices('content')}
                    />
                    <SettingsPushConfirmation
                        devicesCount={devicesCount}
                        isOpen={isOpen}
                        storeCount={storeCount}
                        percentage={percentage}
                        pushToAllDevices={pushToAllDevices('settings')}
                    />
                </>
            )}
        </>
    )
}
